"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MultiPoint = void 0;
const angle_1 = require("../unit/angle");
const geometry_util_1 = require("./geometry/geometry.util");
const vector_1 = require("./vector");
class MultiPoint {
    /**
    * Projects a vector into the plane of a wire defined by two points, and translates the result into 2D.
    *
    * This function is used as the first step of calculating wire sag.
    * Steps:
    *  1. Compute the 3D plane defined by `startPoint` and `endPoint` (the two attachment points of the wire).
    *    - The plane is perpendicular to ground (sag is defined by gravity).
    *  2. Cast `ray` into the plane, to create a new vector `midPoint` where `ray` intersects the plane.
    *  3. Translate `startPoint`, `endPoint`, and `midPoint` into 2D.
    *
    * @param startPoint 3D attachment point-a of the wire.
    * @param endPoint 3D attachment point-b of the wire.
    * @param ray Device orientation which defines midpoint when intersecting with plane.
    * @returns Each point translated into 2D, with origin around `startPoint`.
    */
    static getSagPoints2D(startPoint, endPoint, ray) {
        // No need to correct bearing because points are not plotted (relative angles stay the same)
        const startBearing = MultiPoint.getRelativeBearing(startPoint);
        const endBearing = MultiPoint.getRelativeBearing(endPoint);
        const rayBearing = MultiPoint.getRelativeBearing(ray);
        const vA = vector_1.Vector.distanceBearingPitchToCartesian(startPoint.distance, startBearing, startPoint.orientation.pitch);
        const vB = vector_1.Vector.distanceBearingPitchToCartesian(endPoint.distance, endBearing, endPoint.orientation.pitch);
        const verticalAxis = new vector_1.Vector(0, 0, 1);
        const vBDelta = vB.subtract(vA);
        const planeNormal = vBDelta.cross(verticalAxis);
        const endVector = vector_1.Vector.distanceBearingPitchToCartesian(1.0, rayBearing, ray.orientation.pitch);
        const lineVector = {
            start: new vector_1.Vector(0, 0, 0),
            end: endVector
        };
        const vC = (0, geometry_util_1.linePlaneIntersection)(lineVector, vA, planeNormal);
        const vCDelta = vC.subtract(vA);
        const endX = Math.sqrt(vBDelta.x * vBDelta.x + vBDelta.y * vBDelta.y);
        const endY = vBDelta.z;
        const midX = Math.sqrt(vCDelta.x * vCDelta.x + vCDelta.y * vCDelta.y);
        const midY = vCDelta.z;
        return {
            straightLineDistance: vBDelta.norm(),
            start: new vector_1.Vector(0.0, 0.0, 0.0),
            end: new vector_1.Vector(endX, endY, 0.0),
            mid: new vector_1.Vector(midX, midY, 0.0)
        };
    }
    /**
    *   Takes two ordered bearings and return the angle between.
    *   For example for parameters 0, 90 returns 90
    *   while parameters 90, 0 return -90
    *   @param a bearing in radians
    *   @param b other bearing in radians
    *   @return signed angle between the two bearings radians
    */
    static angleBetweenBearings(a, b) {
        const av = new vector_1.Vector(Math.sin(a), Math.cos(a), 0);
        const bv = new vector_1.Vector(Math.sin(b), Math.cos(b), 0);
        const planeNormal = new vector_1.Vector(0, 0, 1);
        let unsignedAngle = av.dot(bv);
        unsignedAngle = Math.acos(unsignedAngle);
        const cross = av.cross(bv);
        const sign = planeNormal.dot(cross);
        return sign < 0 ? unsignedAngle : -unsignedAngle;
    }
    /**
    *   intended to optimize the gyroscope and magnetomerter based bearings, in point to point mode
    *   to obtain the most accurate  point to point length, and azimuth
    *   @param trueBearingOfPointOne true bearing (magnetometer based) for point A in radians
    *   @param trueBearingOfPointTwo true bearing (magnetometer based) for point B in radians
    *   @param relativeBearingOfPointOne relative bearing (gyroscope based) for point A in radians
    *   @param relativeBearingOfPointTwo relative bearing (gyroscope based) for point B in radians
    *   @return double array with two points for the resultant bearings after fusion
    */
    static getCorrectedBearings(trueBearingOfPointOne, trueBearingOfPointTwo, relativeBearingOfPointOne, relativeBearingOfPointTwo) {
        //get most accurate angle between bearings
        const angleBetweenVectors = MultiPoint.angleBetweenBearings(relativeBearingOfPointOne, relativeBearingOfPointTwo);
        //construct naive orientation of the two bearing with respect to true north
        const one = trueBearingOfPointOne;
        const optimalPointTwoBearing = angle_1.IkeAngle.normalize(trueBearingOfPointOne + angleBetweenVectors);
        // find the angular difference between trueBearingOfPointTwo and optimalPointTwoBearing
        // which assuming the gyros are perfect is the magnetic error, divide by 2 to average it
        const differential = MultiPoint.angleBetweenBearings(optimalPointTwoBearing, trueBearingOfPointTwo) / 2.0;
        // rotate by that average error to reduce error in the azimuth, while
        // using the gyros for the optimal angle between and hence vector length
        return {
            a: angle_1.IkeAngle.normalize(one + differential),
            b: angle_1.IkeAngle.normalize(optimalPointTwoBearing + differential)
        };
    }
    static getBearings(a, b) {
        if (a.raw == null ||
            b.raw == null ||
            a.raw.orientation == null ||
            b.raw.orientation == null ||
            a.raw.orientation.yaw == null ||
            b.raw.orientation.yaw == null) {
            return {
                a: a.orientation.bearing,
                b: b.orientation.bearing
            };
        }
        return MultiPoint.getCorrectedBearings(a.orientation.bearing, b.orientation.bearing, a.raw.orientation.yaw, b.raw.orientation.yaw);
    }
    /**
     * Get bearing value between 0 and 2Pi.
     *
     * Prefer to use `yaw` from gyroscope reading.
     * Gyroscope is considered more accurate than `bearing` from magnetometer.
     */
    static getRelativeBearing(a) {
        if (a.raw == null ||
            a.raw.orientation == null ||
            a.raw.orientation.yaw == null) {
            return angle_1.IkeAngle.normalize(a.orientation.bearing);
        }
        return angle_1.IkeAngle.normalize(a.raw.orientation.yaw);
    }
    /**
    * Calculate the distance, bearing and pitch from pointA to pointB
    *
    * returns
    *   horizontal - Horizontal distance between points (Meters)
    *   vertical - Vertical distance between points (Meters)
    *   distance - Distance between points (Meters)
    *   bearing - Offset from north (between 0  2PI) (Radians)
    *   pitch - Offset from horizontal (Between -PI and PI)
    */
    static PointToPoint(a, b) {
        const bearing = MultiPoint.getBearings(a, b);
        const vA = vector_1.Vector.distanceBearingPitchToCartesian(a.distance, bearing.a, a.orientation.pitch);
        const vB = vector_1.Vector.distanceBearingPitchToCartesian(b.distance, bearing.b, b.orientation.pitch);
        const vDiff = vB.subtract(vA);
        const comp = vector_1.Vector.distanceBearingAndPitchFromCartesian(vDiff);
        const horizontal = Math.sqrt(vDiff.x * vDiff.x + vDiff.y * vDiff.y);
        const vertical = vDiff.z;
        return {
            horizontal,
            vertical,
            distance: comp.x,
            bearing: angle_1.IkeAngle.normalize(comp.y),
            pitch: comp.z
        };
    }
    static Points(points) {
        const [a, b] = points;
        return MultiPoint.PointToPoint(a, b);
    }
}
exports.MultiPoint = MultiPoint;
//# sourceMappingURL=multipoint.js.map